export class WebSocketService {
    private static instance: WebSocketService | null = null;
    private ws: WebSocket | null = null;
    private readonly url: string;
    private messageHandler: ((message: any) => void) | null = null;
    private errorHandler: ((error: Error) => void) | null = null;
    private reconnectAttempts = 0;
    private readonly maxReconnectAttempts = 5;
    private readonly reconnectTimeout = 3000;
    private reconnectTimer: NodeJS.Timeout | null = null;
    private isConnecting = false;

    private constructor(url: string, messageHandler?: (message: any) => void, errorHandler?: (error: Error) => void) {
        this.url = url;
        this.messageHandler = messageHandler || null;
        this.errorHandler = errorHandler || null;
    }

    static getInstance(url: string, messageHandler?: (message: any) => void, errorHandler?: (error: Error) => void): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService(url, messageHandler, errorHandler);
        } else {
            if (messageHandler) {
                WebSocketService.instance.setMessageHandler(messageHandler);
            }
            if (errorHandler) {
                WebSocketService.instance.setErrorHandler(errorHandler);
            }
        }
        return WebSocketService.instance;
    }

    public static destroyInstance() {
        if (WebSocketService.instance) {
            WebSocketService.instance.disconnect();
            WebSocketService.instance = null;
        }
    }

    connect() {
        if (this.isConnecting) return;
        this.isConnecting = true;

        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
            this.isConnecting = false;
            this.reconnectAttempts = 0;
        };

        this.ws.onmessage = (event) => {
            this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            this.isConnecting = false;
            if (this.errorHandler) {
                this.errorHandler(new Error('WebSocket connection failed'));
            }
        };
    }

    setMessageHandler(handler: (message: any) => void) {
        this.messageHandler = handler;
    }

    setErrorHandler(handler: (error: Error) => void) {
        this.errorHandler = handler;
    }

    private handleMessage(data: string) {
        try {
            if (this.messageHandler) {
                this.messageHandler(data);
            }
        } catch (error) {
            console.error('Error handling message:', error);
        }
    }

    private attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('Max reconnection attempts reached');
            return;
        }

        this.reconnectAttempts++;
        console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        this.reconnectTimer = setTimeout(() => {
            this.connect();
        }, this.reconnectTimeout);
    }

    disconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
}
